import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Facebook, Mail } from 'lucide-react';
import { ContactFormModal } from './ContactFormModal';
import { CookiePreferencesButton } from './CookieBanner';
import { useSiteContent } from '../contexts/SiteContentContext';

export const Footer = () => {
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('booking_call');
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  const { get } = useSiteContent();
  const contactDetails = get('contact_details');
  const socialData = get('social_links');
  const contactEmail = contactDetails?.email || 'hello@iamnatashasimone.com';
  const socialLinks = socialData?.instagram ? socialData : { instagram: 'https://www.instagram.com/iamnatashasimone/', facebook: 'https://www.facebook.com/iamnatashasimone/' };

  if (location.pathname.startsWith('/admin')) return null;

  const openForm = (type) => { setFormType(type); setShowForm(true); };

  return (
    <>
      <ContactFormModal isOpen={showForm} onClose={() => setShowForm(false)} inquiryType={formType} sourcePage="footer" />
      <footer className="bg-[#FBF8F4]" data-testid="site-footer">
        <div className="container-padding pt-16 md:pt-20 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            <div className="lg:col-span-1">
              <Link to="/" data-testid="footer-logo" className="inline-block mb-5">
                <img src="/images/Logo/Natasha Simone website (4).png" alt="Natasha Simone" className="h-9 w-auto" />
              </Link>
              <p className="text-sm text-[#7A7A7A] leading-relaxed max-w-xs">Empowering beauty professionals to build thriving businesses and discover their full potential.</p>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.15em] font-medium text-[#E84993] mb-6">Navigate</h4>
              <ul className="space-y-4">
                <li><Link to="/" className="text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300" data-testid="footer-link-home">Home</Link></li>
                <li><Link to="/business" className="text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300" data-testid="footer-link-business">Business Programs</Link></li>
                <li><Link to="/personal" className="text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300" data-testid="footer-link-personal">Personal Development</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.15em] font-medium text-[#E84993] mb-6">Programs</h4>
              <ul className="space-y-4">
                <li><Link to="/business" className="text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300" data-testid="footer-link-dis">Diverse Intelligence Systems</Link></li>
                <li><Link to="/personal" className="text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300" data-testid="footer-link-beauty">The Beauty of You</Link></li>
                <li><button onClick={() => openForm('workshop')} className="text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300" data-testid="footer-link-workshops">Workshops & Speaking</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.15em] font-medium text-[#E84993] mb-6">Connect</h4>
              <div className="space-y-4">
                <a href={`mailto:${contactEmail}`} className="flex items-center gap-2 text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300" data-testid="footer-email">
                  <Mail className="w-4 h-4" /> {contactEmail}
                </a>
                <div className="flex items-center gap-3 pt-2">
                  {socialLinks.instagram && (
                    <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-[#F0E6F6] text-[#6A6A6A] hover:bg-[#E84993] hover:text-white hover:border-[#E84993] hover:-translate-y-1 transition-all duration-300" data-testid="footer-instagram" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
                  )}
                  {socialLinks.facebook && (
                    <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full border border-[#F0E6F6] text-[#6A6A6A] hover:bg-[#E84993] hover:text-white hover:border-[#E84993] hover:-translate-y-1 transition-all duration-300" data-testid="footer-facebook" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-[#F0E6F6]">
          <div className="container-padding py-8 text-center space-y-4">
            <p className="text-sm text-[#6A6A6A]" data-testid="footer-company">
              Yellow Mango Production | 401 N. Ashley Dr., Suite # 173011 | Tampa, Florida 33602
            </p>
            <p className="text-xs text-[#9A9A9A] max-w-xl mx-auto leading-relaxed">
              Established in 1996 training beauty professionals and future professionals both in the United States and internationally.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-[#9A9A9A]" data-testid="footer-legal-links">
              <span>Copyright &copy; {currentYear} I Am Natasha Simone</span>
              <span className="hidden sm:inline">|</span>
              <Link to="/privacy" className="hover:text-[#E84993] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-[#E84993] transition-colors" data-testid="footer-terms-link">Terms and Conditions</Link>
              <span>|</span>
              <CookiePreferencesButton className="text-xs" />
              <span>|</span>
              <Link to="/privacy#california-rights" className="hover:text-[#E84993] transition-colors" data-testid="footer-dnss-link">Do Not Sell or Share My Personal Information</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
